"use client";

import type { ImageProps } from "@/app/directories/shop/[clientSlug]/types";
import { cn } from "@frontend/shared/lib/utils";
import { cva } from "class-variance-authority";
import { motion } from "framer-motion";
import { useKeenSlider } from "keen-slider/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { CustomCarouselSlide } from "./CustomCarouselSlide";

const dotVariants = cva(
  "w-4 h-1 rounded-full mx-1 p-1 cursor-pointer focus:outline-none",
  {
    variants: {
      active: {
        true: "lg:bg-black sm:bg-white w-8",
        false: "bg-gray-300",
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

const navigationButtonVariants = cva(
  "lg:block sm:hidden w-12 h-12 absolute top-1/2 -translate-y-1/2 fill-white cursor-pointer rounded-full flex items-center justify-center",
  {
    variants: {
      hasSecondaryBackground: {
        true: "", // No border when secondary background color exists
        false: "bg-white border border-black", // White bg with black border when no secondary color
      },
    },
    defaultVariants: {
      hasSecondaryBackground: false,
    },
  },
);

export const CustomCarousel = ({
  items,
  style,
  imagePosition,
  secondaryBackgroundColor,
}: {
  items: {
    title: string;
    subtitle: string;
    description: string;
    image: ImageProps;
  }[];
  style: string;
  imagePosition: "left" | "right";
  secondaryBackgroundColor?: string | null;
}) => {
  const isMinimal = false;

  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [loaded, setLoaded] = useState(false);

  const hasCustomSecondaryColor =
    secondaryBackgroundColor && secondaryBackgroundColor !== "#ffffff";

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    slides: {
      spacing: 10,
      origin: "center",
    },
    breakpoints: {
      "(min-width: 320px)": {
        slides: {
          perView: 1.1,
          spacing: 10,
          origin: "center",
        },
      },
      "(min-width: 768px)": {
        slides: {
          perView: 1.0,
          spacing: 10,
          origin: "center",
        },
      },
    },
    created() {
      setLoaded(true);
    },
  });

  return (
    <div
      className={cn(
        "relative w-full h-fit lg:px-[96px] sm:px-0 sm:py-12 ",
        style,
      )}
    >
      <div ref={sliderRef} className="keen-slider h-full relative ">
        {items.map((item, idx) => {
          return (
            <CustomCarouselSlide
              key={`carousel-slide-${item.title}-${idx}`}
              item={item}
              isMinimal={isMinimal}
              imagePosition={imagePosition}
            />
          );
        })}

        {loaded && instanceRef.current && (
          <motion.div
            className="flex justify-start mt-0 absolute lg:bottom-4 sm:bottom-2 lg:left-[96px] md:left-[18px] sm:left-[32px]"
            layoutId="carousel-dots"
          >
            {[
              ...Array(
                instanceRef.current.track.details?.slides?.length,
              ).keys(),
            ].map((idx) => {
              return (
                <motion.button
                  type="button"
                  key={idx}
                  onClick={() => {
                    instanceRef.current?.moveToIdx(idx);
                  }}
                  className={cn(dotVariants({ active: currentSlide === idx }))}
                  layoutId={`dot-${idx}`}
                />
              );
            })}
          </motion.div>
        )}
      </div>
      {loaded && instanceRef.current && (
        <>
          {currentSlide !== 0 && (
            <button
              type="button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                instanceRef.current?.prev();
              }}
              disabled={currentSlide === 0}
              className={cn(
                navigationButtonVariants({
                  hasSecondaryBackground: !!hasCustomSecondaryColor,
                }),
                "left-8",
              )}
              style={{
                backgroundColor: hasCustomSecondaryColor
                  ? secondaryBackgroundColor
                  : undefined,
              }}
            >
              <ChevronLeft className="ml-[10px]" />
            </button>
          )}

          {currentSlide !==
            instanceRef.current.track.details?.slides?.length - 1 && (
            <button
              type="button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                instanceRef.current?.next();
              }}
              className={cn(
                navigationButtonVariants({
                  hasSecondaryBackground: !!hasCustomSecondaryColor,
                }),
                "left-auto right-8",
              )}
              style={{
                backgroundColor: hasCustomSecondaryColor
                  ? secondaryBackgroundColor
                  : undefined,
              }}
            >
              <ChevronRight className="ml-[10px]" />
            </button>
          )}
        </>
      )}
    </div>
  );
};
